<template>
  <div class="landing-page">
    <!-- 2. Hero Section -->
    <section class="hero-section">
      <div class="banner">
        <img src="@/assets/home/banner.png" class="background-img" alt="Kooyii">
        <div class="text-content">
          <h1 v-html="t('hero.title')"></h1>
        </div>
      </div>

      <div class="hero-products-container animate-fade-up" style="transition-delay: 0.2s">
        <div class="product-group">
          <img src="@/assets/home/card.png">
        </div>
      </div>

      <div class="hero-description animate-fade-up" style="transition-delay: 0.4s">
        <p>{{ t('hero.desc') }}</p>
      </div>
    </section>

    <!-- 3. Control Your Way -->
    <section class="section text-center bg-light overflow-hidden">
      <h2 class="section-title" v-scroll-reveal>{{ t('protocol.title') }}</h2>
      <div class="grid-container grid-3">
        <div class="card" v-for="(card, index) in controlCards" :key="index" v-scroll-reveal
          :style="{ transitionDelay: `${index * 150}ms` }">
          <img class="card-icon" :src="getAssetIcon(card.icon)" />
          <h3>{{ card.title }}</h3>
          <p>{{ card.desc }}</p>
        </div>
      </div>
    </section>

    <!-- 4. Advanced Features -->
    <section class="section overflow-hidden">
      <h2 class="section-title text-center" v-scroll-reveal>{{ t('services.title') }}</h2>
      <div class="feature-rows">
        <div class="feature-row" v-for="(feature, index) in advancedFeatures" :key="index"
          :class="{ 'reverse-row': index % 2 !== 0 }">
          <div class="feature-content" v-scroll-reveal :class="index % 2 === 0 ? 'slide-right' : 'slide-left'">
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.desc }}</p>
          </div>
          <img :src="getAssetImage(feature.icon)" class="feature-image" v-scroll-reveal.zoom
            :class="index % 2 === 0 ? 'slide-left' : 'slide-right'" />
        </div>
      </div>
    </section>

    <!-- 5. Get Started -->
    <section class="section bg-light overflow-hidden">
      <h2 class="section-title text-center" v-scroll-reveal>{{ t('software.title') }}</h2>
      <div class="grid-container grid-3">
        <div class="step-card" v-for="(step, index) in steps" :key="index" v-scroll-reveal
          :style="{ transitionDelay: `${index * 100}ms`, cursor: 'pointer' }" :class="{ 'is-clickable': step.link }"
          @click="handleCardClick(step)">
          <img class="step-img" :src="getAssetIcon(step.icon)" />
          <h4>{{ step.title }}</h4>
          <p>{{ step.desc }}</p>

          <div v-if="step.qrCode" class="qr-overlay">
            <img :src="step.qrCode" alt="QR" class="qr-img" />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
// ✨ 1. 引入 computed
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const iconModules = import.meta.glob('../assets/icons/*.png', { eager: true });
const imageModules = import.meta.glob('../assets/home/*.png', { eager: true });

const getAssetIcon = (name) => {
  const globPath = `../assets/icons/${name}.png`;
  if (iconModules[globPath]) {
    return iconModules[globPath].default;
  } else {
    console.warn(`Icon ${name} not found, displaying default.`);
    return;
  }
}

const getAssetImage = (name) => {
  const globPath = `../assets/home/${name}.png`;
  if (imageModules[globPath]) {
    return imageModules[globPath].default;
  } else {
    console.warn(`Image ${name} not found, displaying default.`);
    return;
  }
}

const vScrollReveal = {
  mounted: (el, binding) => {
    const isZoom = binding.modifiers.zoom
    el.classList.add(isZoom ? 'reveal-zoom-before-enter' : 'reveal-before-enter')

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          el.classList.add('reveal-enter')
          observer.unobserve(el)
        }
      })
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    })
    observer.observe(el)
  }
}

// ✨ 2. 使用 computed 包装数据数组，内部使用 t() 映射语言包的 key
const controlCards = computed(() => [
  { icon: 'zigbee', color: '#ff4d4f', title: 'Zigbee 3.0', desc: t('protocol.cards.zigbee') },
  { icon: 'wifi', color: '#1890ff', title: 'Wi-Fi', desc: t('protocol.cards.wifi') },
  { icon: '433', color: '#1890ff', title: '433MHz', desc: t('protocol.cards.rf433') },
  { icon: 'modbus', color: '#1890ff', title: 'Modbus', desc: t('protocol.cards.modbus') },
  { icon: 'knx', color: '#1890ff', title: 'KNX', desc: t('protocol.cards.knx') },
  { icon: 'bluetooth', color: '#1890ff', title: 'Bluetooth', desc: t('protocol.cards.bluetooth') }
])

const advancedFeatures = computed(() => [
  { icon: 'card_home', title: t('services.features.smartHome.title'), desc: t('services.features.smartHome.desc') },
  { icon: 'card_safe', title: t('services.features.security.title'), desc: t('services.features.security.desc') },
  { icon: 'card_hotel', title: t('services.features.hotel.title'), desc: t('services.features.hotel.desc') },
  { icon: 'card_building', title: t('services.features.building.title'), desc: t('services.features.building.desc') }
])

const steps = computed(() => [
  { icon: 'cms', title: t('software.steps.cms.title'), desc: t('software.steps.cms.desc'), link: 'https://new-cms.wifigsmalarm.com' },
  { icon: 'kooyii', title: t('software.steps.kooyii.title'), desc: t('software.steps.kooyii.desc'), qrCode: 'https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://vuejs.org/' },
  { icon: 'old', title: t('software.steps.elderly.title'), desc: t('software.steps.elderly.desc') },
  { icon: 'hotel', title: t('software.steps.hotel.title'), desc: t('software.steps.hotel.desc') },
  { icon: 'building', title: t('software.steps.building.title'), desc: t('software.steps.building.desc'), link: 'https://new-cms.wifigsmalarm.com' },
  { icon: 'more', title: t('software.steps.more.title'), desc: t('software.steps.more.desc') }
])

const handleCardClick = (step) => {
  if (step.link) {
    window.open(step.link, '_blank')
  }
}
</script>

<style scoped>
/* --- 全局样式 --- */
.landing-page {

  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  color: #333;
  line-height: 1.6;
  overflow-x: hidden;
}

.placeholder-img {
  background-color: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
}

.text-center {
  text-align: center;
}

.bg-light {
  background-color: #f9fafb;
}

.section {
  padding: 80px 20px;
}

.section-title {
  font-size: 2.2rem;
  margin-bottom: 50px;
  font-weight: 700;
}

.overflow-hidden {
  overflow: hidden;
}

/* 防止左右飞入动画导致页面出现水平滚动条 */

/* =========================================
   动画核心 CSS 样式 
   ========================================= */

/* 1. 基础的【向上浮现】（文字、卡片常用） */
.reveal-before-enter {
  opacity: 0;
  transform: translateY(40px);
  /* 使用 ease-out 让动画结束时更柔和 */
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

/* 2. 【缩放+向上浮现】（图片、大图块常用） */
.reveal-zoom-before-enter {
  opacity: 0;
  transform: translateY(40px) scale(0.95);
  transition: opacity 1s cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1);
}

/* （可选补充）左右滑入修饰类，结合上面的基础类使用 */
.reveal-before-enter.slide-right {
  transform: translateX(-40px);
}

.reveal-before-enter.slide-left {
  transform: translateX(40px);
}

.reveal-zoom-before-enter.slide-right {
  transform: translateX(-40px) scale(0.95);
}

.reveal-zoom-before-enter.slide-left {
  transform: translateX(40px) scale(0.95);
}

/* 3. 统一的【动画激活态】 */
.reveal-enter {
  opacity: 1 !important;
  transform: translate(0, 0) scale(1) !important;
}



.sub-nav {
  display: flex;
  justify-content: center;
  gap: 40px;
  padding: 20px 0;
}

.sub-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 0.85rem;
  color: #666;
}

.sub-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-bottom: 8px;
}

.hero-section {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 60px;
}



/* 1. 父容器 Banner 设置为相对定位 */
.banner {
  position: relative;
  width: 100%;
  height: 80vh;
  /* 假设是全屏 Banner */
  overflow: hidden;
}

/* 背景图处理同上 */
.background-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -1;
}

/* 2. **核心调整：使用绝对定位精确控制** */
.text-content {
  position: absolute;
  /* **关键调整：设置距离底部的距离（底部对齐）** */
  /* 调整这个值（例如 10%）可以控制文字的高低 */
  bottom: 50%;

  /* 水平居中方法 */
  left: 50%;
  transform: translateX(-50%);
  /* 水平偏移 50% 实现精确居中 */

  text-align: center;
  /* 文字内部居中 */
  color: white;
  z-index: 1;
  width: 90%;
  /* 给一个最大宽度，防止文字太多时贴边 */
}

/* 进一步精细化文字样式 */
.text-content h1 {
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
}

.text-content p {
  font-size: 1.2rem;
  font-weight: normal;
}

.hero-text h1 {
  font-size: 2.8rem;
  color: white;
  text-align: center;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.hero-products-container {
  width: 100%;
  max-width: 900px;
  margin-top: -150px;
  position: relative;
  z-index: 10;
  padding: 0 20px;
}

.product-group {
  width: 100%;
}

.product-group img {
  width: 100%;
  height: 300px;
}

.hero-description {
  max-width: 700px;
  text-align: center;
  margin-top: 40px;
  color: #555;
  font-size: 1.1rem;
  padding: 0 20px;
}

.grid-container {
  display: grid;
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
}

.grid-3 {
  grid-template-columns: repeat(3, 1fr);
}

.grid-4 {
  grid-template-columns: repeat(4, 1fr);
}

.card {
  background: white;
  padding: 40px 30px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
}

.card-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
}

.card h3 {
  margin-bottom: 15px;
  font-size: 1.3rem;
}

.card p {
  color: #666;
  font-size: 0.95rem;
}

.feature-rows {
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 60px;
}

.feature-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 60px;
}

.feature-row.reverse-row {
  flex-direction: row-reverse;
}

.feature-content {
  flex: 1;
}

.feature-content h3 {
  font-size: 1.8rem;
  margin-bottom: 20px;
}

.feature-content p {
  color: #555;
  font-size: 1.1rem;
}

.feature-image {
  flex: 1;
  height: 350px;
  border-radius: 16px;
  width: 100%;
}

.step-card {
  background: white;
  padding: 30px 20px;
  border-radius: 16px;
  border: 1px solid #eaeaea;

  /* 原有的悬浮和相对定位属性保持不变 */
  position: relative;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  /* ✨ 新增：让卡片内的标题(h4)和描述(p)也居中对齐，视觉上更统一 ✨ */
  text-align: center;
}

/* 修改图片占位符样式 */
.step-img {
  width: 60px;
  height: 60px;
  border-radius: 12px;

  /* ✨ 关键修改：上边距0，左右自动平分实现居中，下边距20px ✨ */
  margin: 0 auto 20px auto;

  /* 原有的占位符居中逻辑（如果替换为真实图片，以下两行可删） */
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-card h4 {
  font-size: 1.1rem;
  margin-bottom: 10px;
}

.step-card p {
  font-size: 0.85rem;
  color: #666;
}

@media (max-width: 992px) {
  .hero-text h1 {
    font-size: 2.2rem;
  }

  .grid-3 {
    grid-template-columns: repeat(2, 1fr);
  }

  .grid-4 {
    grid-template-columns: repeat(2, 1fr);
  }

  .feature-row,
  .feature-row.reverse-row {
    gap: 40px;
  }
}

@media (max-width: 768px) {
  .top-nav {
    flex-direction: column;
    gap: 15px;
    padding: 15px;
  }

  .nav-links {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
  }

  .hero-background {
    height: 400px;
  }

  .hero-products-container {
    margin-top: -100px;
  }

  .product-group {
    height: 200px;
  }

  .grid-3,
  .grid-4 {
    grid-template-columns: 1fr;
  }

  .feature-row,
  .feature-row.reverse-row {
    flex-direction: column-reverse;
    text-align: center;
    gap: 30px;
  }

  /* 移动端移除左右滑动，全部改为上下滑动以免体验撕裂 */
  .reveal-before-enter.slide-right,
  .reveal-before-enter.slide-left {
    transform: translateY(40px);
  }

  .feature-image {
    height: 250px;
  }
}

/* =========================================
   ✨ 语言选择器专属样式 ✨
========================================= */
.lang-selector {
  position: relative;
  margin-left: 25px;
  font-size: 0.95rem;
  cursor: pointer;
  user-select: none;
}

.current-lang {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #fff;
  transition: opacity 0.2s;
}

.current-lang:hover {
  opacity: 0.8;
}

/* 箭头动画 */
.arrow {
  font-size: 0.7rem;
  font-style: normal;
  transition: transform 0.3s ease;
}

.arrow-up {
  transform: rotate(180deg);
}

/* 下拉菜单面板 */
.lang-dropdown {
  position: absolute;
  top: 180%;
  /* 距离顶部的距离 */
  right: 0;
  background: #2a2a2a;
  /* 深色主题下拉框，贴合顶部导航的黑色 */
  color: #fff;
  list-style: none;
  padding: 8px 0;
  margin: 0;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  min-width: 180px;
  z-index: 200;
}

.lang-dropdown li {
  padding: 12px 20px;
  transition: background 0.2s, color 0.2s;
}

.lang-dropdown li:hover {
  background: #3a3a3a;
}

/* 当前选中语言的高亮样式 */
.lang-dropdown li.active {
  color: #409eff;
  /* 蓝色高亮 */
  font-weight: 600;
  background: #333;
}

/* Vue <transition name="dropdown"> 动画 */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.qr-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(4px);

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  /* ✨ 关键修改：强制隐藏，并屏蔽鼠标事件 ✨ */
  opacity: 0 !important;
  visibility: hidden !important;
  pointer-events: none !important;

  transform: scale(0.95);
  transition: all 0.3s ease;
  z-index: 10;
}

.step-card:hover .qr-overlay {
  /* ✨ 关键修改：悬浮时强制显示，并恢复鼠标事件 ✨ */
  opacity: 1 !important;
  visibility: visible !important;
  pointer-events: auto !important;
  transform: scale(1);
}
</style>